/**
 * RequirePermission (F84/F85) — guard de ruta que valida que el user
 * logueado tenga el permiso requerido. Si no, redirige a la primera
 * ruta accesible para él (ej. cajero → /pagos en vez de /usuarios).
 */
import { Navigate } from "react-router-dom";
import { useAuthStore } from "@/lib/auth";

// Orden de prioridad: si el user no tiene el permiso de la ruta solicitada,
// busca la primera de esta lista que sí pueda ver.
const FALLBACK_ROUTES: { path: string; perm: string }[] = [
  { path: "/", perm: "dashboard.read" },
  { path: "/pagos", perm: "pagos.read" },
  { path: "/cobranza", perm: "cobranza.read" },
  { path: "/clientes", perm: "clientes.read" },
  { path: "/facturas", perm: "facturas.read" },
  { path: "/contratos", perm: "contratos.read" },
];

type Props = {
  perm: string;
  /** rc40: bloquea acceso aunque tenga el perm, si su rol está en esta lista.
   * Pa rutas admin donde un rol con permisos heredados (ej. cajero con pagos.read)
   * NO debe entrar a la vista global (puede usar el dashboard rol-específico).
   */
  denyRoles?: string[];
  children: React.ReactNode;
};

export function RequirePermission({ perm, denyRoles, children }: Props) {
  const permissions = useAuthStore((s) => s.permissions);
  const user = useAuthStore((s) => s.user);

  // rc40: deny por rol tiene prioridad sobre el perm check
  if (denyRoles && user?.rol?.nombre && denyRoles.includes(user.rol.nombre)) {
    return <Navigate to="/" replace />;
  }

  if (permissions.includes(perm)) return <>{children}</>;

  // Buscar fallback accesible
  const fallback = FALLBACK_ROUTES.find((r) => permissions.includes(r.perm));
  return <Navigate to={fallback?.path ?? "/login"} replace />;
}
